import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Dialog, DialogContent, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
// COMPONENTS
import PdfViewerHeader from '../../../../components/PdfViewer/PdfViewerHeader';
import PdfViewer from '../../../../components/PdfViewer/PdfViewer';
import NavigationBarHeader from '../../UserDashboard/components/NavigationBarHeader';
// CONSTANTS
import { scales } from '../Resources';
import { TP_COLORS } from '../../../../constants';
import { getSessionState } from '../../../../store/SessionSlice';
import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../../store/AccessLimitSlice';

interface PdfViewerModalProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  pageNumber: string;
  allowWatchVideo?: boolean;
  toggleWatchVideoModal?: () => void;
  onChangePageModal: (pageNo: string) => void;
}

interface IState {
  initialLoading: boolean;
  pageNo: string;
  fileUrl: string;
  totalPages: number;
  scale: number;
}

const CustomTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} timeout={500} />;
});

const initialState: IState = {
  initialLoading: true,
  pageNo: '1',
  fileUrl: '',
  totalPages: 0,
  scale: 1,
};

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  open,
  onClose,
  pageNumber,
  fileUrl,
  allowWatchVideo = false,
  toggleWatchVideoModal = undefined,
  onChangePageModal,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Store
  const sessionState = useAppSelector(getSessionState);
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  // State
  const [state, setState] = useState<IState>(initialState);
  const [isWhiteboardVisible, setIsWhiteboardVisible] = useState(false);

  const { session } = sessionState;
  const { usage, limitation } = accessLimitState;
  const authUser = authState.user;

  const isLoading = state.initialLoading;
  const pdfContainerStyles: React.CSSProperties = {
    padding: '30px 15px',
    minHeight: '100vh',
    maxWidth: 'calc(100vw - 60px)',
    overflow: 'auto',
    outline: 'none',
  };

  const isPremiumModal =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  useEffect(() => {
    if (open) {
      setState({
        initialLoading: false,
        fileUrl,
        pageNo: pageNumber,
        totalPages: 0,
        scale: scales[3],
      });
    }
  }, [open]);

  const handleClose = () => {
    setState(initialState);

    onClose();
  };

  const onDocumentLoadSuccess = (numPages) => {
    setState((prevState) => ({
      ...prevState,
      initialLoading: false,
      totalPages: numPages,
    }));
  };

  const onChangePage = (pageNo: string) => {
    const isNext = Number(state.pageNo) < Number(pageNo);
    if (
      isNext &&
      limitation &&
      limitation.slideViews &&
      limitation.slideViews > 0 &&
      usage.slideViews + 2 >= limitation.slideViews
    ) {
      dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
    } else {
      setState((prevState) => ({
        ...prevState,
        pageNo,
      }));
      onChangePageModal(pageNo);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.keyCode === 39 &&
      Number(state.pageNo) < state.totalPages + session.questions.length + 1
    ) {
      onChangePage((Number(state.pageNo) + 1).toString());
    } else if (e.keyCode === 37 && Number(state.pageNo) > 1) {
      onChangePage((Number(state.pageNo) - 1).toString());
    }
  };

  const handleScaleChange = (type: 'inc' | 'dec') => {
    const _index = scales.findIndex((sc) => sc === state.scale);
    if (
      type === 'inc' &&
      state.scale !== scales[scales.length - 1] &&
      _index > -1
    ) {
      setState((prevState) => ({
        ...prevState,
        scale: scales[_index + 1],
      }));
    } else if (type === 'dec' && state.scale !== scales[0] && _index > -1) {
      setState((prevState) => ({
        ...prevState,
        scale: scales[_index - 1],
      }));
    }
  };

  const onAttempt = () => {
    navigate(location.pathname.replace('/resources/', '/'));
  };

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={CustomTransition}
      PaperProps={{ style: { backgroundColor: '#333333' } }}
    >
      <NavigationBarHeader />
      <div>
        <PdfViewerHeader
          isInFullScreen
          allowWatchVideo={allowWatchVideo}
          toggleWatchVideoModal={toggleWatchVideoModal}
          onChangePage={onChangePage}
          onFullScreenToggle={handleClose}
          onScaleChange={handleScaleChange}
          pageNo={state.pageNo}
          scale={state.scale}
          fileUrl={state.fileUrl}
          totalPages={state.totalPages + session.questions.length}
          isWhiteboardVisible={isWhiteboardVisible}
          setIsWhiteboardVisible={setIsWhiteboardVisible}
          // downloadUrl={fileUrl}
          appBarProps={{
            position: 'fixed',
            sx: { top: { xs: '56px', sm: '64px' } },
          }}
        />
      </div>
      <div style={{ minHeight: '100px' }} />
      <DialogContent
        style={{
          color: TP_COLORS.white,
          backgroundColor: isWhiteboardVisible ? 'white' : '',
        }}
      >
        {open ? (
          <div>
            {!isLoading ? (
              <Box
                tabIndex={0}
                onKeyDown={onKeyDown}
                style={{
                  ...pdfContainerStyles,
                  ...(isWhiteboardVisible ? { visibility: 'hidden' } : {}),
                }}
              >
                <PdfViewer
                  fileUrl={state.fileUrl}
                  scale={state.scale}
                  selectedPageNumber={state.pageNo}
                  onAttempt={onAttempt}
                  onLoadSuccess={(numPages) => onDocumentLoadSuccess(numPages)}
                  pageWidth={
                    window.document.body.clientWidth - 60 > 1200
                      ? 1200
                      : window.document.body.clientWidth - 150
                  }
                  session={session}
                  onChangePage={onChangePage}
                />
              </Box>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerModal;
